/**
 * /dashboard — Individual candidate's attempt history (Story 31-32).
 *
 * Consumes the shared `<AttemptsTable>` and `<AttemptsSummaryCards>` components
 * with `role="candidate"`. Columns: Test, Date, Duration, Score, Result.
 * No numeric eyebrow. Cursor pagination via "Load more".
 *
 * Issue 15-frontend-gaps/15 — unified attempts-list schema.
 */

import { useEffect, useState, useCallback, type ReactElement } from 'react';
import { Link } from 'react-router-dom';
import {
  listMyAttempts,
  type AttemptSummary,
} from '../../state/analytics/api.ts';
import { Shell } from '@/components/shell';
import { AttemptsTable } from '../../components/attempts/AttemptsTable.tsx';
import { AttemptsSummaryCards } from '../../components/attempts/AttemptsSummaryCards.tsx';
import type { AttemptsTableRow } from '../../components/attempts/types.ts';
import { AvailabilityChip } from '@/components/availability/AvailabilityChip';
import { relativeCountdown } from '../../lib/availability';

const API_BASE = '';

/** Map the API response shape to the canonical row shape. */
function toRow(a: AttemptSummary): AttemptsTableRow {
  return {
    attempt_id: a.attempt_id,
    candidate_name: a.candidate_name,
    candidate_email: a.candidate_email,
    test_title: a.test_title,
    company_name: null,
    date: a.completed_at ?? '',
    duration_seconds: a.duration_seconds,
    score_percentage: a.score,
    passed: a.passed,
  };
}

export function DashboardPage(): ReactElement {
  const [items, setItems] = useState<AttemptSummary[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback((cursor?: string): void => {
    cursor ? setLoadingMore(true) : setLoading(true);
    setError(null);

    void (async () => {
      try {
        const res = await listMyAttempts(API_BASE, { limit: 25, cursor });
        if (!res.ok) {
          setError(res.error.message);
          return;
        }
        setItems((prev) => (cursor ? [...prev, ...res.data.items] : res.data.items));
        setNextCursor(res.data.next_cursor);
      } catch {
        setError('Unable to load attempts. Please try again.');
      } finally {
        setLoading(false);
        setLoadingMore(false);
      }
    })();
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const rows = items.map(toRow);

  // Compute summary stats from loaded rows
  const completedRows = rows.filter(
    (r) => r.score_percentage !== null,
  );
  const passedCount = completedRows.filter((r) => r.passed).length;
  const avgScore =
    completedRows.length > 0
      ? completedRows.reduce((s, r) => s + (r.score_percentage ?? 0), 0) /
        completedRows.length
      : 0;

  return (
    <Shell mode='individual' densityMode='editorial'>
      <p className="font-mono text-mono text-muted-2">MY DASHBOARD</p>
      <h1 className="mt-2 font-sans text-headline font-semibold tracking-tight">
        Attempt history
      </h1>

      {rows.length > 0 && (
        <div className="mt-6">
          <AttemptsSummaryCards
            role="candidate"
            totalAttempts={rows.length}
            passedCount={passedCount}
            averageScore={avgScore}
          />
        </div>
      )}

      <div className="mt-6">
        <AttemptsTable
          role="candidate"
          rows={rows}
          loading={loading}
          loadingMore={loadingMore}
          hasMore={!!nextCursor}
          onLoadMore={() => load(nextCursor ?? undefined)}
          error={error}
          onRetry={() => load()}
          errorFallbackHref="/catalogue"
          emptyState={{
            ctaLabel: 'Browse catalogue',
            ctaHref: '/catalogue',
          }}
        />
      </div>

      {/* J-X22 — Retake countdown chips for attempts under resit lockout. */}
      {items.filter((a) => a.retake_available_at).length > 0 && (
        <div className="mt-6 space-y-2">
          <p className="font-mono text-eyebrow uppercase text-muted">Retake cooldowns</p>
          {items
            .filter((a): a is AttemptSummary & { retake_available_at: string } =>
              typeof a.retake_available_at === 'string' && a.retake_available_at.length > 0,
            )
            .map((a) => (
              <div key={a.attempt_id} className="flex items-center gap-3">
                <span className="font-sans text-small text-ink">{a.test_title}</span>
                <AvailabilityChip
                  state={{
                    kind: 'cooldown',
                    pivot_iso: a.retake_available_at,
                    closed_reason: null,
                  }}
                />
                <span className="font-mono text-eyebrow text-muted">
                  {relativeCountdown(a.retake_available_at)}
                </span>
              </div>
            ))}
        </div>
      )}

      {/* Secondary actions for empty-state discoverability */}
      {!loading && !error && rows.length === 0 && (
        <div className="mt-4 flex flex-wrap gap-3">
          <Link
            to="/companies/new"
            className="rounded-md border border-line-2 px-5 py-2.5 text-sm font-medium text-ink hover:bg-bg-warm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2"
          >
            Create company
          </Link>
        </div>
      )}
    </Shell>
  );
}
